import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  createComment,
  deleteComment,
  fetchComments,
  updateComment,
} from "../../../../../../api/occurrenceComments";
import { getUserProfile } from "../../../../../../configuration/login/loginSlice";
import { OccurrenceComment } from "../../../../../../models/occurrence";

const parseUtcIsoStringToDate = (value: string): Date | undefined => {
  let normalized = value;
  const hasTimezone = /Z$/.test(normalized) || /([+-]\d{2}:\d{2}|[+-]\d{2}\d{2})$/.test(normalized);
  if (!hasTimezone) normalized = `${normalized}Z`;
  normalized = normalized.replace(
    /(\.\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})$)/,
    "$1",
  );
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const normalizeComment = (raw: OccurrenceComment): OccurrenceComment => ({
  ...raw,
  commentedAt:
    raw.commentedAt instanceof Date
      ? raw.commentedAt
      : typeof raw.commentedAt === "string"
        ? parseUtcIsoStringToDate(raw.commentedAt)
        : undefined,
});

export const useOccurrenceComments = (occurrenceId: string) => {
  const userProfile = useSelector(getUserProfile);
  const currentUserId = userProfile?.sub?.split(":")[1] ?? "";

  const [comments, setComments] = useState<OccurrenceComment[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [newCommentText, setNewCommentText] = useState("");
  const [editingCommentUuid, setEditingCommentUuid] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [submittingNew, setSubmittingNew] = useState(false);
  const [savingEditUuid, setSavingEditUuid] = useState<string | null>(null);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);

  const loadComments = useCallback(async () => {
    if (!occurrenceId) {
      setComments([]);
      setTotalElements(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    const data = await fetchComments(occurrenceId);
    const list = (data.content ?? []).map(normalizeComment);
    setComments(list);
    setTotalElements(data.totalElements ?? list.length);
    setLoading(false);
  }, [occurrenceId]);

  useEffect(() => {
    void loadComments();
  }, [loadComments]);

  useEffect(() => {
    setExpanded(true);
    setEditingCommentUuid(null);
    setEditDraft("");
  }, [occurrenceId]);

  const submitNewComment = async () => {
    const trimmed = newCommentText.trim();
    if (!occurrenceId || !trimmed || submittingNew) return;
    setSubmittingNew(true);
    const created = await createComment(occurrenceId, trimmed);
    setSubmittingNew(false);
    if (created.uuid) {
      setComments((prev) => [...prev, normalizeComment(created)]);
      setTotalElements((n) => n + 1);
      setNewCommentText("");
    } else {
      await loadComments();
      setNewCommentText("");
    }
  };

  const beginEdit = (comment: OccurrenceComment) => {
    const id = comment.uuid;
    if (!id) return;
    setEditingCommentUuid(id);
    setEditDraft(comment.comments ?? "");
  };

  const cancelEdit = () => {
    setEditingCommentUuid(null);
    setEditDraft("");
  };

  const saveEdit = async () => {
    if (!occurrenceId || !editingCommentUuid) return;
    const trimmed = editDraft.trim();
    if (!trimmed) return;
    setSavingEditUuid(editingCommentUuid);
    const updated = await updateComment(occurrenceId, editingCommentUuid, trimmed);
    setSavingEditUuid(null);
    if (updated.uuid) {
      setComments((prev) =>
        prev.map((c) => (c.uuid === editingCommentUuid ? normalizeComment(updated) : c)),
      );
      cancelEdit();
    } else {
      await loadComments();
      cancelEdit();
    }
  };

  const removeComment = async (commentId: string) => {
    if (!occurrenceId || !commentId) return;
    setDeletingUuid(commentId);
    await deleteComment(occurrenceId, commentId);
    setDeletingUuid(null);
    await loadComments();
  };

  const isCommentAuthor = (comment: OccurrenceComment) =>
    Boolean(
      comment.authorId && currentUserId && comment.authorId === currentUserId,
    );

  return {
    comments,
    itemCount: totalElements,
    loading,
    expanded,
    setExpanded,
    newCommentText,
    setNewCommentText,
    submittingNew,
    editingCommentUuid,
    editDraft,
    setEditDraft,
    savingEditUuid,
    deletingUuid,
    submitNewComment,
    beginEdit,
    cancelEdit,
    saveEdit,
    removeComment,
    isCommentAuthor,
    reloadComments: loadComments,
  };
};
